import styled, { css, keyframes } from 'styled-components';

import { CONTAINER_MAX, CONTENT_MAX_WIDTH } from '@/constants/breakpoints';

export const flexCenterAligned = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const flexColumn = css`
  display: flex;
  flex-direction: column;
`;

export const flexRow = css`
  display: flex;
  flex-direction: row;
`;

export const flexBetween = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const ellipsisText = css`
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const scrollStyles = css`
  &::-webkit-scrollbar {
    background-color: transparent;
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    width: 4px;
    height: 59px;
    border-radius: 7px;
    background-color: #b1b8c6;
  }
`;

export const scrollHidden = css`
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
`;

export const SpaceColumn = styled.div<{ $gap?: number; $center?: boolean }>`
  ${flexColumn};
  gap: ${({ $gap }) => $gap ?? 0}px;
  width: inherit;
  ${({ $center }) =>
    $center && 'align-items: center; justify-content: center;'};
`;

export const SpaceRow = styled.div<{ $gap?: number; $center?: boolean }>`
  ${flexRow};
  gap: ${({ $gap }) => $gap ?? 0}px;
  ${({ $center }) =>
    $center && 'align-items: center; justify-content: center;'};
`;

type transitionKeys = 'visibility' | 'transform' | 'opacity';
type transitionType = Partial<Record<transitionKeys, boolean>> | undefined;

export const openingEffect = (
  offset: number = -10,
  transition: transitionType = {
    opacity: true,
    transform: true,
    visibility: true
  }
) => css<{
  $isOpen: boolean;
}>`
  transition: ${Object.entries(transition ?? [])
    .map(([key, value]) => (value ? `${key} 0.3s ease` : ''))
    .filter(Boolean)
    .join(', ')};

  ${({ $isOpen }) =>
    $isOpen
      ? css`
          visibility: visible;
          opacity: 1;
          transform: translateY(0);
        `
      : css`
          visibility: hidden;
          opacity: 0;
          transform: translateY(${offset}px);
        `};
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: 200px 0;
  }
`;

export const commonSkeletonStyles = css`
  background: linear-gradient(
    90deg,
    var(--border-secondary) 25%,
    var(--border-main) 50%,
    var(--border-secondary) 75%
  );
  background-size: 400px 100%;
  animation: ${shimmer} 1.5s infinite;
`;

export const containerPaddingInline = css`
  /* ${CONTENT_MAX_WIDTH} is the outer frame width, gutters INCLUDED. Above it the
     (100% - cap) / 2 centering term wins; we add the gutter back so the usable
     content caps at cap - 2 * gutter and the gutter stays visible inside the
     frame. Below the cap the term is <= 0, so the plain gutter applies. */
  padding-inline: max(
    var(--px-desktop),
    (100% - ${CONTENT_MAX_WIDTH}) / 2 + var(--px-desktop)
  );

  @media (max-width: ${CONTAINER_MAX.desktop}) {
    padding-inline: var(--px-tablet-l);
  }

  @media (max-width: ${CONTAINER_MAX.tabletL}) {
    padding-inline: var(--px-tablet);
  }

  @media (max-width: ${CONTAINER_MAX.tablet}) {
    padding-inline: var(--px-mobile);
  }

  @media (max-width: ${CONTAINER_MAX.mobileM}) {
    padding-inline: var(--px-mobile-s);
  }
`;

// For full-bleed fixed/sticky bars (BottomActionBar and similar): the bar's
// background spans the viewport while its inner content is centered within
// CONTENT_MAX_WIDTH. Above the cap the (100% - cap) / 2 term wins; below it the
// term is <= 0, so the bar's own `minPadding` side gutter applies.
export const cappedPaddingInline = (minPadding: string) => css`
  padding-inline: max(
    ${minPadding},
    (100% - ${CONTENT_MAX_WIDTH}) / 2 + ${minPadding}
  );
`;

export const decorationLink = css`
  text-decoration-line: underline;
  text-decoration-color: currentColor;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  -webkit-text-decoration-skip: none;
  text-decoration-skip: none;

  text-underline-offset: 2px;
  text-decoration-thickness: 1px;

  &:hover {
    text-decoration-color: currentColor;
  }
`;

export const commonLinkStyles = css`
  transition: color 0.3s ease;

  &:hover {
    color: var(--custom-yellow);
  }
`;
